/**
 * Creature rigs: a creature as data rather than code. A rig is a tree of
 * parts, each with a pivot in its parent's frame, a depth, and the shapes
 * drawn from that pivot in named colours (a palette says what each colour
 * is, per creature). A pose turns and shifts parts each frame.
 *
 * The same rig is drawn two ways: straight to a 2D canvas (RigCanvas: the
 * wildlife preview page, and baking) and as textured quads in the game
 * (RigMesh). Parts that never move (`moves` on neither them nor an
 * ancestor) are baked together into one picture per depth band; a moving
 * part is baked to its own quad, hinged at its pivot. Colours in `glow`
 * are brighter than white (they bloom), so they stay as real geometry.
 *
 * Joints: hinge a limb from the middle of a disc (or the round end of a
 * capsule) sunk inside the part it hangs from, and no gap opens however it
 * swings. Nothing in here knows about three.js or the DOM.
 */

export type Vec = readonly [number, number];
/** A colour as r, g, b in 0..1 (over 1 glows). */
export type Color = readonly [number, number, number];
export type Palette = Readonly<Record<string, Color>>;

export type PathOp =
  | readonly ["M", number, number]
  | readonly ["L", number, number]
  | readonly ["Q", number, number, number, number]
  | readonly ["C", number, number, number, number, number, number]
  | readonly ["Z"];

export type Form =
  /** A filled outline. */
  | { readonly kind: "path"; readonly ops: readonly PathOp[] }
  /** A line this wide, round ended. */
  | { readonly kind: "stroke"; readonly ops: readonly PathOp[]; readonly width: number }
  /** A disc (an ellipse if `ry`), turned `rot`. */
  | { readonly kind: "disc"; readonly r: number; readonly ry?: number; readonly rot?: number }
  /** A round-ended bar from the origin to `to`: a limb, hinged at the origin. */
  | { readonly kind: "capsule"; readonly to: Vec; readonly width: number }
  /**
   * Pixel art (see pixelArt.ts): `box` painted once at `ppu` pixels a unit by
   * `paint` (onto a canvas that size, its top-left at the box's top-left),
   * then drawn with hard pixel edges. Its shape's colour isn't used; `from`
   * is the vector shapes it was painted from, if it was (see pixelRig).
   */
  | { readonly kind: "pixels"; readonly ppu: number; readonly box: Bounds; readonly paint: (ctx: CanvasRenderingContext2D, palette: Palette) => void; readonly from?: readonly Shape[] };

export interface Shape {
  /** A colour's name in the palette. */
  readonly color: string;
  /** Where the form sits from the part's pivot. */
  readonly at?: Vec;
  readonly form: Form;
  /** See-through, this much (1: solid). A glow this way is added to what's behind it. */
  readonly alpha?: number;
}

export interface Part {
  readonly name: string;
  /** Declared before this part; none: hung from the rig itself. */
  readonly parent?: string;
  /** Its pivot, in its parent's frame. */
  readonly at: Vec;
  /** How it's turned at rest. */
  readonly rot?: number;
  /** Drawing order: higher is nearer. */
  readonly z: number;
  /** Posed each frame (so it can't be baked in with its neighbours). */
  readonly moves?: boolean;
  /**
   * It bends rather than turning stiff: along the line from its pivot to
   * here, most of the curve near the root (pose `bend`), so it stays joined
   * to what it grows from. A long neck. Moves.
   */
  readonly spine?: Vec;
  readonly shapes: readonly Shape[];
}

export interface Rig {
  readonly parts: readonly Part[];
  /** Colours that glow: never tinted by the light, and kept as geometry. */
  readonly glow?: readonly string[];
}

/** How a part is placed this frame, from its rest. */
export interface Placement {
  rot?: number;
  x?: number;
  y?: number;
  sx?: number;
  sy?: number;
  /** How far a part with a spine curls along it (radians at the tip, + anticlockwise). */
  bend?: number;
}
export type Pose = Readonly<Record<string, Placement | undefined>>;
export const REST: Pose = {};

/** Something that poses a rig each frame from how its creature is moving. */
export interface Animator<G> {
  step(dt: number, g: G): Pose;
}

/** How a beast on the ground is moving. */
export interface Gait {
  /** Along the ground (blocks/s). */
  speed: number;
  eating: boolean;
}

// ------------------------------------------------------------ affine maps

/** A 2D affine map as the canvas has it: x' = a x + c y + e, y' = b x + d y + f. */
export type Affine = readonly [number, number, number, number, number, number];
export const IDENTITY: Affine = [1, 0, 0, 1, 0, 0];

export function compose(p: Affine, l: Affine): Affine {
  return [p[0] * l[0] + p[2] * l[1], p[1] * l[0] + p[3] * l[1], p[0] * l[2] + p[2] * l[3], p[1] * l[2] + p[3] * l[3], p[0] * l[4] + p[2] * l[5] + p[4], p[1] * l[4] + p[3] * l[5] + p[5]];
}

export function placed(x: number, y: number, rot = 0, sx = 1, sy = 1): Affine {
  const c = Math.cos(rot);
  const s = Math.sin(rot);
  return [c * sx, s * sx, -s * sy, c * sy, x, y];
}

export function apply(m: Affine, x: number, y: number): [number, number] {
  return [m[0] * x + m[2] * y + m[4], m[1] * x + m[3] * y + m[5]];
}

// ------------------------------------------------------------ bending

/** Points sampled along a bent spine. */
const SPINE_STEPS = 24;

/**
 * A part bent along its spine (from its pivot to `spine`) by `amount`: the
 * spine turns `amount * (1 - (1 - s)⁴)` by the fraction `s` along it, so
 * it flexes mostly at the root (as a neck does) and runs on nearly straight. What's
 * across the spine is carried round with it; what's behind the root stays
 * put, and what's past the tip follows the tip stiffly.
 */
export class Bend {
  private readonly len: number;
  private readonly ux: number;
  private readonly uy: number;
  /** Where the bent spine is at each step. */
  private readonly xs: number[] = [0];
  private readonly ys: number[] = [0];

  constructor(
    spine: Vec,
    private readonly amount: number,
  ) {
    this.len = Math.hypot(spine[0], spine[1]);
    this.ux = spine[0] / this.len;
    this.uy = spine[1] / this.len;
    const d = this.len / SPINE_STEPS;
    for (let i = 0; i < SPINE_STEPS; i++) {
      const a = this.turn((i + 0.5) / SPINE_STEPS);
      const c = Math.cos(a);
      const s = Math.sin(a);
      this.xs.push(this.xs[i]! + (c * this.ux - s * this.uy) * d);
      this.ys.push(this.ys[i]! + (s * this.ux + c * this.uy) * d);
    }
  }

  private turn(s: number): number {
    return this.amount * (1 - (1 - s) ** 4);
  }

  /** The stiff map that carries what's at (x, y) (and what's beside it across the spine) to where the bend puts it. */
  frame(x: number, y: number): Affine {
    const s = Math.max(0, Math.min(1, (x * this.ux + y * this.uy) / this.len));
    const k = s * SPINE_STEPS;
    const i = Math.min(SPINE_STEPS - 1, Math.floor(k));
    const f = k - i;
    const cx = this.xs[i]! + (this.xs[i + 1]! - this.xs[i]!) * f;
    const cy = this.ys[i]! + (this.ys[i + 1]! - this.ys[i]!) * f;
    const a = this.turn(s);
    const c = Math.cos(a);
    const n = Math.sin(a);
    // Turn about the straight spine's point there, then move it to the bent one.
    const px = s * this.len * this.ux;
    const py = s * this.len * this.uy;
    return [c, n, -n, c, cx - (c * px - n * py), cy - (n * px + c * py)];
  }

  point(x: number, y: number): [number, number] {
    return apply(this.frame(x, y), x, y);
  }
}

/** How a part is bent in `pose`, if it has a spine and is bent at all. */
export function bendOf(part: Part, pose: Pose): Bend | null {
  const amount = pose[part.name]?.bend ?? 0;
  return part.spine && amount ? new Bend(part.spine, amount) : null;
}

// ------------------------------------------------------------ bounds

export interface Bounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export function emptyBounds(): Bounds {
  return { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
}

export function grow(b: Bounds, x: number, y: number, pad = 0): void {
  b.minX = Math.min(b.minX, x - pad);
  b.minY = Math.min(b.minY, y - pad);
  b.maxX = Math.max(b.maxX, x + pad);
  b.maxY = Math.max(b.maxY, y + pad);
}

export function isEmpty(b: Bounds): boolean {
  return !(b.maxX > b.minX && b.maxY > b.minY);
}

/** The points a path passes through or is pulled toward (a bound on where it goes). */
export function pathPoints(ops: readonly PathOp[]): [number, number][] {
  const pts: [number, number][] = [];
  for (const op of ops) for (let i = 1; i + 1 < op.length; i += 2) pts.push([op[i] as number, op[i + 1] as number]);
  return pts;
}

/** Where a shape reaches, in its part's frame. */
export function shapeBounds(s: Shape): Bounds {
  const b = emptyBounds();
  const [ox, oy] = s.at ?? [0, 0];
  const f = s.form;
  if (f.kind === "pixels") {
    grow(b, ox + f.box.minX, oy + f.box.minY);
    grow(b, ox + f.box.maxX, oy + f.box.maxY);
  } else if (f.kind === "path" || f.kind === "stroke") {
    const pad = f.kind === "stroke" ? f.width / 2 : 0;
    for (const [x, y] of pathPoints(f.ops)) grow(b, ox + x, oy + y, pad);
  } else if (f.kind === "disc") {
    const r = Math.max(f.r, f.ry ?? f.r);
    grow(b, ox, oy, r);
  } else {
    grow(b, ox, oy, f.width / 2);
    grow(b, ox + f.to[0], oy + f.to[1], f.width / 2);
  }
  return b;
}

export function transformBounds(b: Bounds, m: Affine): Bounds {
  const out = emptyBounds();
  for (const [x, y] of [[b.minX, b.minY], [b.maxX, b.minY], [b.minX, b.maxY], [b.maxX, b.maxY]] as const) {
    const [px, py] = apply(m, x, y);
    grow(out, px, py);
  }
  return out;
}

export function union(into: Bounds, b: Bounds): void {
  grow(into, b.minX, b.minY);
  grow(into, b.maxX, b.maxY);
}

// ------------------------------------------------------------ the resolved rig

export interface RigPart {
  readonly part: Part;
  readonly index: number;
  readonly parent: RigPart | null;
  /** Posed, or hung from something posed: drawn on its own, hinged. */
  readonly articulated: boolean;
}

/** A still picture of the parts that never move, between the moving ones in depth. */
export type Layer = { readonly kind: "band"; readonly parts: readonly RigPart[]; readonly z: number } | { readonly kind: "part"; readonly part: RigPart };

/** A rig checked and indexed: parts in declaration order, and in drawing order. */
export class RigModel {
  readonly parts: readonly RigPart[];
  /** Back to front. */
  readonly order: readonly RigPart[];
  readonly glow: ReadonlySet<string>;
  private readonly byName = new Map<string, RigPart>();

  constructor(readonly rig: Rig) {
    const parts: RigPart[] = [];
    for (const [index, part] of rig.parts.entries()) {
      if (this.byName.has(part.name)) throw new Error(`rig: two parts named ${part.name}`);
      const parent = part.parent === undefined ? null : this.byName.get(part.parent);
      if (parent === undefined) throw new Error(`rig: ${part.name} hangs from ${part.parent}, which isn't declared before it`);
      const p: RigPart = { part, index, parent, articulated: !!part.moves || !!part.spine || !!parent?.articulated };
      parts.push(p);
      this.byName.set(part.name, p);
    }
    this.parts = parts;
    this.order = [...parts].sort((a, b) => a.part.z - b.part.z || a.index - b.index);
    this.glow = new Set(rig.glow ?? []);
  }

  part(name: string): RigPart {
    const p = this.byName.get(name);
    if (!p) throw new Error(`rig: no part ${name}`);
    return p;
  }

  /** Each part's map from its frame to the rig's, in `pose` (by part index). A part hung from a bent one rides the bend where its pivot is. */
  affines(pose: Pose): Affine[] {
    const out: Affine[] = [];
    const bends = this.parts.map((p) => bendOf(p.part, pose));
    for (const p of this.parts) {
      const pl = pose[p.part.name];
      const local = placed(p.part.at[0] + (pl?.x ?? 0), p.part.at[1] + (pl?.y ?? 0), (p.part.rot ?? 0) + (pl?.rot ?? 0), pl?.sx ?? 1, pl?.sy ?? 1);
      if (!p.parent) {
        out.push(local);
        continue;
      }
      const bend = bends[p.parent.index];
      const from = out[p.parent.index]!;
      out.push(compose(bend ? compose(from, bend.frame(p.part.at[0], p.part.at[1])) : from, local));
    }
    return out;
  }

  /**
   * How the rig is drawn in the game: back to front, runs of still parts
   * baked together into one band, each moving part on its own. A still
   * part with a glow (drawn as geometry at its own depth) starts a new
   * band, so what's drawn after the glow stays over it.
   */
  layers(): Layer[] {
    const layers: Layer[] = [];
    let band: RigPart[] = [];
    const flush = () => {
      if (band.length) layers.push({ kind: "band", parts: band, z: band[0]!.part.z });
      band = [];
    };
    for (const p of this.order) {
      if (p.articulated) {
        flush();
        layers.push({ kind: "part", part: p });
      } else {
        if (p.part.shapes.some((s) => this.glow.has(s.color))) flush();
        band.push(p);
      }
    }
    flush();
    return layers;
  }
}

// ------------------------------------------------------------ colours and form helpers

/** A CSS colour for `c` lit by `light`. */
export function css(c: Color, light = 1, alpha = 1): string {
  const q = (v: number) => Math.round(Math.max(0, Math.min(1, v * light)) * 255);
  return `rgba(${q(c[0])},${q(c[1])},${q(c[2])},${alpha})`;
}

export function hsl(h: number, s: number, l: number): Color {
  h = ((h % 1) + 1) % 1;
  const f = (n: number) => {
    const k = (n + h * 12) % 12;
    const a = s * Math.min(l, 1 - l);
    return l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
  };
  return [f(0), f(8), f(4)];
}

export const scale = (c: Color, k: number): Color => [c[0] * k, c[1] * k, c[2] * k];
export const mix = (a: Color, b: Color, t: number): Color => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
export const WHITE: Color = [1, 1, 1];

/** A number 0..1 fixed by an id (and `salt`), so each creature looks a little different, the same every time. */
export function idHash(id: string, salt = 0): number {
  let h = 2166136261 ^ salt;
  for (let i = 0; i < id.length; i++) h = Math.imul(h ^ id.charCodeAt(i), 16777619);
  return ((h >>> 0) % 10007) / 10007;
}

/**
 * The band between half an ellipse (a by b) and a flatter one inside it (b
 * times `inner`): shade along the underside, or (`top`) shine along the top.
 */
export function band(a: number, b: number, inner: number, top: boolean, n = 24): Form {
  const ops: PathOp[] = [];
  const dir = top ? -1 : 1;
  for (let i = 0; i <= n; i++) {
    const t = Math.PI + (i / n) * Math.PI * dir;
    ops.push([i ? "L" : "M", a * Math.cos(t), b * Math.sin(t)]);
  }
  for (let i = n; i >= 0; i--) {
    const t = Math.PI + (i / n) * Math.PI * dir;
    ops.push(["L", a * Math.cos(t), b * inner * Math.sin(t)]);
  }
  ops.push(["Z"]);
  return { kind: "path", ops };
}

export const hex = (n: number): Color => [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];

/** An ellipse (rx by ry) at (x, y) as four curves: a piece of a path. */
export function ellipseOps(x: number, y: number, rx: number, ry: number): PathOp[] {
  const kx = 0.5523 * rx;
  const ky = 0.5523 * ry;
  return [
    ["M", x + rx, y],
    ["C", x + rx, y + ky, x + kx, y + ry, x, y + ry],
    ["C", x - kx, y + ry, x - rx, y + ky, x - rx, y],
    ["C", x - rx, y - ky, x - kx, y - ry, x, y - ry],
    ["C", x + kx, y - ry, x + rx, y - ky, x + rx, y],
    ["Z"],
  ];
}

export const circleOps = (x: number, y: number, r: number): PathOp[] => ellipseOps(x, y, r, r);

/** Dots of radius `r` at each of `points`, as one form (one mesh in the game). */
export function dots(points: readonly Vec[], r: number): Form {
  return { kind: "path", ops: points.flatMap(([x, y]) => circleOps(x, y, r)) };
}

/** `ops` turned over, top to bottom. */
export function flipY(ops: readonly PathOp[]): PathOp[] {
  return ops.map((op) => op.map((v, i) => (i > 0 && i % 2 === 0 ? -(v as number) : v)) as unknown as PathOp);
}

/** Points along a path (curves sampled `n` to a piece), for drawing it as a line. */
export function samplePath(ops: readonly PathOp[], n = 8): [number, number][] {
  const pts: [number, number][] = [];
  let x = 0;
  let y = 0;
  let sx = 0;
  let sy = 0;
  for (const op of ops) {
    if (op[0] === "M") {
      [, x, y] = op;
      sx = x;
      sy = y;
      pts.push([x, y]);
    } else if (op[0] === "L") {
      [, x, y] = op;
      pts.push([x, y]);
    } else if (op[0] === "Q") {
      const [, cx, cy, ex, ey] = op;
      for (let i = 1; i <= n; i++) {
        const t = i / n;
        const u = 1 - t;
        pts.push([u * u * x + 2 * u * t * cx + t * t * ex, u * u * y + 2 * u * t * cy + t * t * ey]);
      }
      x = ex;
      y = ey;
    } else if (op[0] === "C") {
      const [, ax, ay, bx, by, ex, ey] = op;
      for (let i = 1; i <= n; i++) {
        const t = i / n;
        const u = 1 - t;
        pts.push([u * u * u * x + 3 * u * u * t * ax + 3 * u * t * t * bx + t * t * t * ex, u * u * u * y + 3 * u * u * t * ay + 3 * u * t * t * by + t * t * t * ey]);
      }
      x = ex;
      y = ey;
    } else {
      pts.push([sx, sy]);
      x = sx;
      y = sy;
    }
  }
  return pts;
}
